import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import BlueButton from '../../Atoms/BlueButton';
import H1 from '../../Atoms/H1';
import * as api from '../../../api';
import { useTranslation } from 'react-i18next';
import Spinner from '../../Atoms/Spinner';

const PageTitle = styled(H1)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
`;

const Container = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
    margin-bottom: 40px;
`;

const StatCard = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    text-align: center;
`;

const StatValue = styled.div`
    font-size: 36px;
    font-weight: bold;
    color: #000;
    margin-bottom: 8px;
`;

const StatLabel = styled.div`
    font-size: 14px;
    color: #666;
    text-transform: uppercase;
`;

const ProgressSection = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const SectionTitle = styled.h2`
    font-size: 24px;
    margin-bottom: 24px;
    color: #000;
`;

const JuryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const JuryCard = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    background: ${props => props.isComplete ? '#f0f9ff' : '#fff'};
`;

const DepartageInfo = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: 12px;
    padding: 4px 8px;
    background: #fff3e0;
    border-radius: 4px;
    font-size: 13px;
    color: #ff9800;
    font-weight: 500;
`;

const JuryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

const JuryName = styled.h3`
    font-size: 18px;
    margin: 0;
    color: #000;
`;

const JuryEmail = styled.p`
    font-size: 14px;
    color: #666;
    margin: 4px 0;
`;

const ProgressBarContainer = styled.div`
    width: 100%;
    height: 24px;
    background: #f0f0f0;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 8px;
`;

const ProgressBar = styled.div`
    height: 100%;
    background: ${props => props.percentage === 100 ? '#4caf50' : '#2196f3'};
    width: ${props => props.percentage}%;
    transition: width 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
    font-weight: bold;
`;

const ProgressInfo = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: #666;
`;

const CompletionBadge = styled.span`
    background: #4caf50;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: bold;
`;

const RefreshButton = styled.button`
    background: #2196f3;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    margin-left: 16px;
    
    &:hover {
        background: #1976d2;
    }
    
    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }
`;

const OverallProgressContainer = styled.div`
    background: #f5f5f5;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
`;

const AutoRefreshToggle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
`;

export default function FDJuryProgress() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [juryData, setJuryData] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const fetchJuryProgress = useCallback(async () => {
        try {
            const response = await api.getJuryProgress();

            if (response && response.data) {
                setJuryData(response.data);
                setLastUpdated(new Date());
            }
        } catch (error) {
            console.error('Error fetching jury progress:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchJuryProgress();
    }, [fetchJuryProgress]);

    useEffect(() => {
        if (autoRefresh) {
            const interval = setInterval(() => {
                fetchJuryProgress();
            }, 30000); // Refresh every 30 seconds

            return () => clearInterval(interval);
        }
    }, [autoRefresh, fetchJuryProgress]);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchJuryProgress();
    };

    if (loading) {
        return (
            <Container>
                <PageTitle>Suivi des Votes du Jury FD100</PageTitle>
                <Spinner />
            </Container>
        );
    }

    if (!juryData) {
        return (
            <Container>
                <PageTitle>Suivi des Votes du Jury FD100</PageTitle>
                <p>Aucune donnée disponible</p>
            </Container>
        );
    }

    const { event, juryProgress, statistics } = juryData;

    return (
        <Container>
            <PageTitle>
                Suivi des Votes du Jury FD100
                <RefreshButton onClick={handleRefresh} disabled={refreshing}>
                    {refreshing ? 'Actualisation...' : 'Actualiser'}
                </RefreshButton>
            </PageTitle>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <Link to="/admin/events/fdcompilationjury" style={{ textDecoration: 'none' }}>
                    <BlueButton>← Retour à la Compilation</BlueButton>
                </Link>
                <AutoRefreshToggle>
                    <input 
                        type="checkbox" 
                        id="autoRefresh" 
                        checked={autoRefresh} 
                        onChange={(e) => setAutoRefresh(e.target.checked)}
                    />
                    <label htmlFor="autoRefresh">Actualisation automatique (30s)</label>
                    <span style={{ marginLeft: '16px', color: '#666', fontSize: '12px' }}>
                        Dernière mise à jour: {lastUpdated.toLocaleTimeString()}
                    </span>
                </AutoRefreshToggle>
            </div>

            <StatsGrid>
                <StatCard>
                    <StatValue>{statistics.totalJuries}</StatValue>
                    <StatLabel>Membres du Jury</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue>{statistics.completeJuries}</StatValue>
                    <StatLabel>Votes Terminés</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue>{statistics.incompleteJuries}</StatValue>
                    <StatLabel>Votes en Cours</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue>{statistics.totalProjects}</StatValue>
                    <StatLabel>Projets à Noter</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue style={{color: '#ff9800'}}>{statistics.totalDepartagePoints || 0}</StatValue>
                    <StatLabel>Points de Départage</StatLabel>
                </StatCard>
            </StatsGrid>

            <OverallProgressContainer>
                <SectionTitle>Progression Globale</SectionTitle>
                <ProgressBarContainer>
                    <ProgressBar percentage={statistics.overallCompletion}>
                        {statistics.overallCompletion}%
                    </ProgressBar>
                </ProgressBarContainer>
                <ProgressInfo>
                    <span>{statistics.totalVotesCast} votes sur {statistics.totalPossibleVotes}</span>
                    <span>{statistics.overallCompletion}% complété</span>
                </ProgressInfo>
            </OverallProgressContainer>

            <ProgressSection>
                <SectionTitle>Détail par Membre du Jury</SectionTitle>
                <JuryList>
                    {juryProgress.map((jury) => (
                        <JuryCard key={jury.id} isComplete={jury.isComplete}>
                            <JuryHeader>
                                <div>
                                    <JuryName>
                                        {jury.name}
                                        {jury.departagePointsGiven > 0 && (
                                            <DepartageInfo>
                                                🎯 {jury.departagePointsGiven} point{jury.departagePointsGiven > 1 ? 's' : ''} de départage
                                            </DepartageInfo>
                                        )}
                                    </JuryName>
                                    <JuryEmail>{jury.email}</JuryEmail>
                                </div>
                                {jury.isComplete && <CompletionBadge>Terminé</CompletionBadge>}
                            </JuryHeader>
                            <ProgressBarContainer>
                                <ProgressBar percentage={jury.completionPercentage}>
                                    {jury.completionPercentage}%
                                </ProgressBar>
                            </ProgressBarContainer>
                            <ProgressInfo>
                                <span>{jury.ratedProjects} / {jury.totalProjects} projets notés</span>
                                <span>{jury.remaining} restants</span>
                            </ProgressInfo>
                        </JuryCard>
                    ))}
                </JuryList>
            </ProgressSection>
        </Container>
    );
}